import React, { useEffect, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Loader2, Save, ShieldCheck, KeyRound, Monitor, ShieldAlert, ShieldOff } from "lucide-react";
import { OwnerTransfer } from "@/components/OwnerTransfer";

// Profile modal: name / email / password + recent sign-in fingerprint + 2FA on/off.
export default function ProfileModal({ onClose }) {
  const { user, setUser, logout } = useAuth();
  const [tab, setTab] = useState("account"); // account | security | sessions
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "",
    current_password: "", new_password: "", confirm_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [tfa, setTfa] = useState({ enabled: false });
  const [tfaSetup, setTfaSetup] = useState(null); // { secret, otpauth }
  const [tfaCode, setTfaCode] = useState("");
  const [tfaBusy, setTfaBusy] = useState(false);
  const [tfaDisablePw, setTfaDisablePw] = useState("");
  const [backupCodes, setBackupCodes] = useState(null); // one-time plaintext codes shown once

  useEffect(() => {
    api.get("/auth/sessions").then(r => setSessions(r.data || [])).catch(() => {});
    api.get("/auth/2fa/status").then(r => setTfa(r.data || { enabled: false })).catch(() => {});
  }, []);

  const submitProfile = async (e) => {
    e.preventDefault();
    if (!form.current_password) return toast.error("Enter your current password to confirm");
    if (form.new_password && form.new_password !== form.confirm_password) return toast.error("New password confirmation doesn't match");
    setSaving(true);
    try {
      const payload = { current_password: form.current_password };
      if (form.name && form.name !== user.name) payload.name = form.name;
      if (form.email && form.email.toLowerCase() !== (user.email || "").toLowerCase()) payload.email = form.email;
      if (form.new_password) payload.new_password = form.new_password;
      const { data } = await api.patch("/auth/me", payload);
      toast.success("Profile updated");
      if (data.reauth_required) {
        toast.message("Sign in again with your new credentials", { duration: 6000 });
        await logout(); window.location.href = "/login"; return;
      }
      setUser({ ...user, name: data.user.name, email: data.user.email });
      onClose?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const startTfa = async () => {
    setTfaBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setTfaSetup(data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setTfaBusy(false); }
  };
  const confirmTfa = async () => {
    if (tfaCode.length < 6) return toast.error("Enter the 6-digit code");
    setTfaBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/confirm", { code: tfaCode });
      toast.success("Two-factor login is now ON");
      setTfa({ enabled: true }); setTfaSetup(null); setTfaCode("");
      if (data.backup_codes) setBackupCodes(data.backup_codes);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Wrong code"); }
    finally { setTfaBusy(false); }
  };
  const regenBackup = async () => {
    if (!confirm("Regenerate backup codes? Your previous codes will stop working immediately.")) return;
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/regenerate");
      setBackupCodes(data.backup_codes || []);
      toast.success("New backup codes issued");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const disableTfa = async () => {
    if (!tfaDisablePw) return toast.error("Enter your current password");
    setTfaBusy(true);
    try {
      await api.post("/auth/2fa/disable", { current_password: tfaDisablePw });
      toast.success("Two-factor login disabled"); setTfa({ enabled: false }); setTfaDisablePw("");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setTfaBusy(false); }
  };

  const tabs = [
    { k: "account", label: "Account" },
    { k: "security", label: "Security" },
    { k: "sessions", label: `Sign-ins${sessions.length?` · ${sessions.length}`:""}` },
    ...(user?.is_owner ? [{ k: "owner", label: "Ownership" }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="profile-modal">
      <div className="w-full max-w-lg border border-white/10 bg-[#0A0A0A] text-white flex flex-col max-h-[92vh]">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Account</div>
            <div className="text-lg font-medium mt-0.5">Edit profile</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10" data-testid="profile-close-btn"><X size={16}/></button>
        </div>
        <div className="px-5 pt-3 flex gap-1 border-b border-white/10">
          {tabs.map(t => (
            <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`profile-tab-${t.k}`}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] border-b-2 ${tab===t.k?"border-emerald-400 text-white":"border-transparent text-zinc-400 hover:text-white"}`}>{t.label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === "account" && (
            <form onSubmit={submitProfile} className="p-5 space-y-4">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</div>
                <input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="profile-name-input"
                  className="w-full mt-1 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm"/>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</div>
                <input type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="profile-email-input"
                  className="w-full mt-1 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm"/>
              </label>
              <div className="pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-emerald-300"><ShieldCheck size={11}/> Confirm identity</div>
                <input type="password" placeholder="Current password (required)"
                  value={form.current_password} onChange={e=>setForm({...form, current_password: e.target.value})} data-testid="profile-current-password"
                  className="w-full mt-2 bg-transparent border border-emerald-500/40 px-3 py-2 outline-none focus:border-emerald-400 text-sm"/>
              </div>
              <div className="pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400"><KeyRound size={11}/> Change password (optional)</div>
                <input type="password" placeholder="New password · min 10 chars"
                  value={form.new_password} onChange={e=>setForm({...form, new_password: e.target.value})} data-testid="profile-new-password"
                  className="w-full mt-2 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm"/>
                <input type="password" placeholder="Confirm new password"
                  value={form.confirm_password} onChange={e=>setForm({...form, confirm_password: e.target.value})} data-testid="profile-confirm-password"
                  className="w-full mt-2 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm"/>
              </div>
              <div className="pt-3 flex items-center gap-3 justify-end">
                <button type="button" onClick={onClose} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
                <button type="submit" disabled={saving} data-testid="profile-save-btn"
                  className="px-4 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400 disabled:opacity-40 flex items-center gap-2">
                  {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Save profile
                </button>
              </div>
            </form>
          )}

          {tab === "security" && (
            <div className="p-5 space-y-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                {tfa.enabled ? <ShieldCheck size={12} className="text-emerald-400"/> : <ShieldAlert size={12} className="text-amber-400"/>}
                Two-factor authentication · <span className={tfa.enabled?"text-emerald-300":"text-amber-300"}>{tfa.enabled?"ON":"OFF"}</span>
              </div>
              {tfa.enabled ? (
                <div className="border border-emerald-500/30 bg-emerald-500/[0.05] p-4">
                  <div className="text-sm">Your account is protected by an authenticator app.</div>
                  {backupCodes && (
                    <div className="mt-4 border border-amber-400/40 bg-amber-400/[0.06] p-3" data-testid="backup-codes">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold">Save these 10 backup codes NOW</div>
                      <div className="text-[11px] text-zinc-400 mt-1">Each works ONCE. Store them somewhere safe — you can't view them again.</div>
                      <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm text-amber-200 tabular">
                        {backupCodes.map((c, i) => <div key={i} className="border border-amber-400/20 bg-black/30 px-3 py-2 tracking-widest text-center">{c}</div>)}
                      </div>
                      <div className="mt-3 flex gap-2 justify-end">
                        <button onClick={()=>{ navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Copied all 10 codes"); }}
                          data-testid="copy-backup-codes"
                          className="px-3 py-1.5 border border-amber-400/40 text-amber-300 text-[10px] uppercase tracking-[0.2em] hover:bg-amber-400/10">Copy all</button>
                        <button onClick={()=>setBackupCodes(null)} className="px-3 py-1.5 border border-white/20 text-[10px] uppercase tracking-[0.2em] hover:bg-white/10">I've saved them</button>
                      </div>
                    </div>
                  )}
                  <button onClick={regenBackup} data-testid="regen-backup-btn"
                    className="mt-4 text-[10px] uppercase tracking-[0.2em] text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 px-3 py-1.5 flex items-center gap-1.5">
                    <KeyRound size={11}/> Regenerate backup codes
                  </button>
                  <div className="mt-4 border-t border-white/10 pt-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Turn OFF two-factor</div>
                    <input type="password" placeholder="Current password"
                      value={tfaDisablePw} onChange={e=>setTfaDisablePw(e.target.value)} data-testid="tfa-disable-password"
                      className="w-full mt-2 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm"/>
                    <button onClick={disableTfa} disabled={tfaBusy} data-testid="tfa-disable-btn"
                      className="mt-3 px-4 py-2 border border-red-500/40 bg-red-500/[0.06] text-red-300 text-[10px] uppercase tracking-[0.2em] hover:bg-red-500/[0.14] disabled:opacity-40 flex items-center gap-2">
                      <ShieldOff size={12}/> Disable 2FA
                    </button>
                  </div>
                </div>
              ) : tfaSetup ? (
                <div className="border border-emerald-500/30 bg-emerald-500/[0.04] p-4 space-y-3">
                  <div className="text-sm">1. Open your authenticator app (Google Authenticator, 1Password, Authy) and add a new account.</div>
                  <div className="text-sm">2. Scan this URI or enter the secret manually:</div>
                  <div className="border border-white/10 bg-black p-3 text-[11px] break-all tabular text-emerald-300" data-testid="tfa-secret">{tfaSetup.secret}</div>
                  <a href={tfaSetup.otpauth} className="text-[11px] text-emerald-300 underline break-all" data-testid="tfa-otpauth">{tfaSetup.otpauth}</a>
                  <div className="text-sm mt-2">3. Enter the 6-digit code from the app to finish:</div>
                  <input value={tfaCode} onChange={e=>setTfaCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                    data-testid="tfa-code" inputMode="numeric" maxLength={6}
                    className="w-full bg-transparent border-2 border-emerald-500/50 px-3 py-2 outline-none focus:border-emerald-400 text-2xl tabular tracking-widest text-center"/>
                  <div className="flex gap-2 pt-1">
                    <button onClick={confirmTfa} disabled={tfaBusy || tfaCode.length!==6} data-testid="tfa-confirm-btn"
                      className="flex-1 px-4 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400 disabled:opacity-40 flex items-center justify-center gap-2">
                      {tfaBusy ? <Loader2 size={13} className="animate-spin"/> : <ShieldCheck size={13}/>} Confirm & enable
                    </button>
                    <button onClick={()=>{ setTfaSetup(null); setTfaCode(""); }} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="border border-white/10 p-4">
                  <div className="text-sm text-zinc-300">Add an extra layer to your sign-in. You&#39;ll enter a 6-digit code from your phone every time you log in — so a stolen password on its own can&#39;t get in.</div>
                  <button onClick={startTfa} disabled={tfaBusy} data-testid="tfa-setup-btn"
                    className="mt-4 px-4 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400 disabled:opacity-40 flex items-center gap-2">
                    {tfaBusy ? <Loader2 size={13} className="animate-spin"/> : <ShieldCheck size={13}/>} Set up two-factor
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === "sessions" && (
            <div className="p-5 space-y-2" data-testid="profile-sessions">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recent sign-ins on this account</div>
              {sessions.length === 0 ? (
                <div className="text-sm text-zinc-500 border border-dashed border-white/10 p-6 text-center">No sign-in history yet.</div>
              ) : sessions.map((s, i) => (
                <div key={s.id || i} className="border border-white/10 p-3 flex items-start gap-3" data-testid={`session-${i}`}>
                  <Monitor size={16} className="text-emerald-400 mt-0.5 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{s.ua_short || "Device"} {i===0 && <span className="ml-2 text-[9px] uppercase tracking-widest text-emerald-400">Current</span>}</div>
                    <div className="text-[11px] text-zinc-500 tabular truncate">IP {s.ip || "?"} · {(s.created_at||"").replace("T"," ").slice(0,16)}</div>
                  </div>
                </div>
              ))}
              <div className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
                Something look wrong? Change your password right away — that will force every device to sign in again.
              </div>
            </div>
          )}
          {tab === "owner" && <OwnerTransfer onDone={onClose}/>}
        </div>
      </div>
    </div>
  );
}
